import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

import { Divider, Icon, Menu, Tag } from '@beek/design-system';

import { BeekUserRole } from 'src/utils/user';
import { useHasActiveSubscriptionQuery } from 'src/graphql/user';
import { userRole } from 'src/app/auth/cookies';
import Avo from 'src/tracking/Avo';
import Const from 'src/config/Const';
import removeUserInfo from 'src/app/auth/removeUserInfo';
import webPlayerSettingsButtonClicked from 'src/tracking/events/webPlayerSettingsButtonClicked';

const renderWebPlayerLink = (
  text: string,
  loading: boolean,
  hasActiveSubscription: boolean,
  trackEvent: () => void
): JSX.Element => {
  if (!loading && hasActiveSubscription) {
    return (
      <div data-testid="webPlayerLink">
        <Menu.Item
          label={text}
          icon={<Icon.WebPlayer size="small" />}
          tag={<Tag.Beta size="large" />}
          onClick={() => trackEvent()}
        />
        <Divider />
      </div>
    );
  }
  return <></>;
};

const AccountMenu = (): JSX.Element => {
  const t = useTranslations('articles');
  const router = useRouter();
  const [role, setRole] = useState(BeekUserRole.creator);

  const logout = useCallback(() => {
    Avo.logOutButtonPressed({
      origin: `User Account Dropdown Menu: ${router.pathname}`,
    });

    removeUserInfo();
    router.push('/');
  }, [router]);

  const trackWebPlayerClick = useCallback(() => {
    webPlayerSettingsButtonClicked({
      origin: `User Account Dropdown Menu: ${router.pathname}`,
    });
    window.location.assign(Const.webPlayerUrl);
  }, [router]);

  useEffect(() => {
    setRole(userRole());
  }, []);

  const { data, loading } = useHasActiveSubscriptionQuery();

  return (
    <Menu label={<Icon.User />} placement="bottomLeft">
      {renderWebPlayerLink(
        t('nav.web_player') as string,
        loading,
        data?.viewer.hasActiveSubscription || false,
        trackWebPlayerClick
      )}
      <Link href="/settings" passHref>
        <Menu.Item
          label={t('nav.settings') as string}
          icon={<Icon.Settings size="small" />}
        />
      </Link>
      {role !== BeekUserRole.creator && (
        <Link href="/settings/manage-subscription" passHref>
          <Menu.Item
            label={t('nav.subscription') as string}
            icon={<Icon.Cards size="small" />}
          />
        </Link>
      )}
      <Divider />
      <Menu.Item
        label={t('nav.help') as string}
        href={Const.supportUrl}
        target="_blank"
        rel="noopener noreferrer"
        icon={<Icon.Question size="small" />}
      />
      <Menu.Item
        label={t('nav.logout') as string}
        onClick={logout}
        icon={<Icon.LogOut size="small" />}
        color="danger"
      />
    </Menu>
  );
};

export default AccountMenu;
